import { withCartSource } from '@/lib/cart-source';
import { createLogger } from '@/lib/logger';
import { shopifyFormatCurrency } from './utils';

type GiftModalStatus =
  | 'not-eligible'
  | 'not-needed'
  | 'user-closed'
  | 'add-multiple'
  | 'user-rejected';

export type GetProductDetailsVariant = {
  id: string;
  title: string;
  price: number;
  image: string;
  compareAtPrice: number | null;
  available: boolean;
  inventoryQuantity: number;
  inventoryPolicy: 'CONTINUE' | 'DENY';
  swatchData: {
    title: string;
    image: string;
    colour: string | null;
    categories: string[];
  };
};

export type GetProductDetailsData = {
  id: string;
  title: string;
  handle: string;
  onlineStoreUrl: string;
  priceRange: string;
  compareAtPriceRange: string;
  variants: GetProductDetailsVariant[];
  options: {
    [key: string]: string[];
  };
  image: string;
  variantOptionsMap: Record<string, string[]>;
};

export type GetProductDetailsResponse =
  | {
      status: 'success';
      data: GetProductDetailsData;
    }
  | {
      status: 'error';
      message: string;
    };

export class SelectOptionsModal extends HTMLElement {
  private apiBaseUrl: string;

  private swatchOptionTemplate: HTMLTemplateElement | null;
  private pillOptionTemplate: HTMLTemplateElement | null;

  private closeButtonElem: HTMLButtonElement | null;
  private imageElem: HTMLImageElement | null;
  private productTitleElem: HTMLHeadingElement | null;
  private variantTitleElem: HTMLHeadingElement | null;
  private regularPriceElem: HTMLSpanElement | null;
  private compareAtPriceElem: HTMLSpanElement | null;

  private optionsFormElem: HTMLFormElement | null;

  private decreaseQuantityButtonElem: HTMLButtonElement | null;
  private quantityInputElem: HTMLInputElement | null;
  private increaseQuantityButtonElem: HTMLButtonElement | null;

  private productFormElem: HTMLFormElement | null;

  private addToCartButtonElem: HTMLButtonElement | null;

  private quantity: number;

  private productId: string | null;
  private productData: GetProductDetailsData | null;
  private productDetailsJson: GetProductDetailsResponse | null;

  private selectedOptions: {
    [key: string]: FormDataEntryValue;
  } = {};

  private selectedVariantId: string;
  private selectedVariant: GetProductDetailsVariant | null;

  private logger = createLogger('select-options-modal');

  private moneyFormat = '$ {{amount}}';

  private getActiveCurrencyCode(): string | null {
    const active = window.Shopify?.currency?.active;
    return typeof active === 'string' && active.trim() !== '' ? active : null;
  }

  private getShopBaseCurrencyCode(): string | null {
    const base = (window.theme as { shopCurrency?: string } | undefined)?.shopCurrency;
    if (typeof base === 'string' && base.trim() !== '') return base;
    return this.getActiveCurrencyCode();
  }

  private getDisplayMoneyFormat(): string {
    const theme = window.theme as
      | {
          moneyWithCurrencyFormat?: string;
          moneyFormat?: string;
          shopCurrency?: string;
        }
      | undefined;

    const baseFormat = theme?.moneyWithCurrencyFormat ?? theme?.moneyFormat ?? this.moneyFormat;

    const active = this.getActiveCurrencyCode();
    const base = this.getShopBaseCurrencyCode();

    if (!active || !base || active === base) return baseFormat;

    // Try to swap the currency code in the existing format (e.g. "AUD" -> "USD")
    return baseFormat.replace(base, active);
  }

  private convertBaseCentsToActiveCents(baseCents: number): number {
    const active = this.getActiveCurrencyCode();
    const base = this.getShopBaseCurrencyCode();

    if (!active || !base || active === base) return Math.round(baseCents);

    const converter = (
      window as unknown as {
        Currency?: {
          convert?: (amount: number, from: string, to: string) => number;
        };
      }
    ).Currency;
    if (!converter || typeof converter.convert !== 'function') return Math.round(baseCents);

    const baseDollars = baseCents / 100;
    try {
      const convertedDollars = converter.convert(baseDollars, base, active);
      if (typeof convertedDollars !== 'number' || Number.isNaN(convertedDollars)) {
        return Math.round(baseCents);
      }
      return Math.round(convertedDollars * 100);
    } catch {
      return Math.round(baseCents);
    }
  }

  constructor() {
    super();

    this.swatchOptionTemplate = null;
    this.pillOptionTemplate = null;

    this.apiBaseUrl = '';

    this.closeButtonElem = null;
    this.imageElem = null;
    this.productTitleElem = null;
    this.variantTitleElem = null;
    this.regularPriceElem = null;
    this.compareAtPriceElem = null;

    this.optionsFormElem = null;

    this.decreaseQuantityButtonElem = null;
    this.quantityInputElem = null;
    this.increaseQuantityButtonElem = null;

    this.productFormElem = null;

    this.addToCartButtonElem = null;

    this.quantity = 1;

    this.productId = this.getAttribute('data-product-id');
    this.productData = null;
    this.productDetailsJson = null;

    this.selectedVariantId = this.getAttribute('data-variant-id') ?? '';
    this.selectedVariant = null;

    this.moneyFormat = this.getDisplayMoneyFormat();
  }

  private decreaseQuantity() {
    this.quantity -= 1;

    // Clamp to 1 as minimum value
    if (this.quantity < 1) this.quantity = 1;

    const quantityInputElem = this.quantityInputElem;
    if (!quantityInputElem) return;
    quantityInputElem.value = this.quantity.toString();
  }

  private increaseQuantity() {
    this.quantity += 1;

    const maxQuantity =
      this.selectedVariant?.inventoryPolicy === 'CONTINUE'
        ? -1
        : (this.selectedVariant?.inventoryQuantity ?? 1);

    if (maxQuantity !== -1 && this.quantity > maxQuantity) this.quantity = maxQuantity;

    const quantityInputElem = this.quantityInputElem;
    if (!quantityInputElem) return;
    quantityInputElem.value = this.quantity.toString();
  }

  private static readonly VISIBLE_CLASS = 'select-options-modal--visible';

  private showPopover(): void {
    this.classList.add(SelectOptionsModal.VISIBLE_CLASS);
    this.setAttribute('aria-hidden', 'false');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    const backdrop = this.querySelector<HTMLElement>('.select-options-modal--backdrop');
    if (backdrop) backdrop.setAttribute('aria-hidden', 'false');
  }

  private hidePopover(): void {
    this.classList.remove(SelectOptionsModal.VISIBLE_CLASS);
    this.setAttribute('aria-hidden', 'true');
    const backdrop = this.querySelector<HTMLElement>('.select-options-modal--backdrop');
    if (backdrop) backdrop.setAttribute('aria-hidden', 'true');
  }

  private onClose() {
    this.hidePopover();
  }

  private async runPurchaseGiftModalFlow(): Promise<{
    status: GiftModalStatus;
  } | null> {
    const manager = window.PurchaseGiftManager;
    if (!manager || typeof manager.showModal !== 'function') return null;

    const selectedVariant = this.selectedVariant;
    const currentVariantId = this.selectedVariantId;

    if (!selectedVariant || !currentVariantId) {
      console.error(
        '[select-options-modal] Missing selected variant data; cannot run PurchaseGiftManager.showModal overrides',
      );
      return manager.showModal();
    }

    const modalViewport =
      document.querySelector<HTMLElement>('.product-gift-modal__viewport') ?? undefined;
    const packMultiple =
      document.querySelector('[data-bundle-option].active')?.getAttribute('data-pack-multiple') ||
      undefined;

    const skipLabel =
      window.theme?.labels?.product_gift_skip_cta_label?.replace(
        '{qty}',
        this.quantity.toString(),
      ) ||
      window.theme?.labels?.addToCart ||
      '';

    const baseCurrencyPriceCents = Math.round(selectedVariant.price * 100);

    return manager.showModal({
      currentVariantId,
      variant: {
        price: baseCurrencyPriceCents,
        inventory_quantity: selectedVariant.inventoryQuantity,
      },
      qty: this.quantity,
      ...(packMultiple ? { packMultiple } : {}),
      ...(modalViewport ? { modalViewport } : {}),
      ...(skipLabel ? { skipLabel } : {}),
    });
  }

  private async onAddToCart() {
    this.logger.log(`Adding ${this.quantity} of variant ${this.selectedVariantId} to cart`);

    let shouldHidePopover = true;

    try {
      const giftModalResult = await this.runPurchaseGiftModalFlow();

      if (giftModalResult?.status === 'user-closed') {
        shouldHidePopover = false;
        return;
      }

      if (giftModalResult?.status === 'add-multiple') {
        const requiredAmount = Number(window.theme?.gift_product?.required_amount ?? 0);
        if (requiredAmount > 0) {
          this.quantity = requiredAmount;
          const quantityInputElem = this.quantityInputElem;
          if (quantityInputElem) {
            quantityInputElem.value = this.quantity.toString();
          }
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.selectedVariantId,
          quantity: this.quantity,
          properties: withCartSource(null, 'select-options-modal:onAddToCart'),
        }),
      });

      this.logger.log('Add to cart response', response);
    } catch (error) {
      this.logger.error('Add to cart failed', error);
    } finally {
      if (shouldHidePopover) {
        this.hidePopover();
      }
    }
  }

  private findVariantForSelectedOptions(): string {
    const selectedOptionValues = Object.values(this.selectedOptions) as string[];

    if (!this.productData) return '';

    const match = Object.entries(this.productData.variantOptionsMap).find(
      ([_variantId, options]) => {
        return options.every((option) => selectedOptionValues.includes(option));
      },
    );

    if (!match) {
      return '';
    }

    const [matchingVariantId, _matchingVariantValues] = match;

    this.logger.log('Found matching variant', matchingVariantId);
    this.selectedVariantId = matchingVariantId;

    return matchingVariantId;
  }

  private updateSelectedValue(optionName: string, optionValue: string) {
    const optionNameElem = this.querySelector<HTMLFieldSetElement>(
      `[data-option-name="${optionName}"]`,
    );

    if (!optionNameElem) return;

    const selectedValueElem = optionNameElem.querySelector<HTMLSpanElement>(
      `[data-option-selected-value]`,
    );

    if (!selectedValueElem) return;

    selectedValueElem.textContent = optionValue.trim();
  }

  private onOptionChange() {
    const optionsFormElem = this.optionsFormElem;
    if (!optionsFormElem) return;
    const formData = new FormData(optionsFormElem);
    const nextSelectedOptions = Object.fromEntries(formData.entries());

    // Avoid redundant work if nothing changed (e.g. clicking an already-selected option)
    if (JSON.stringify(nextSelectedOptions) === JSON.stringify(this.selectedOptions)) {
      return;
    }

    this.selectedOptions = nextSelectedOptions;

    this.logger.log('Selected options:', this.selectedOptions);

    Object.keys(this.selectedOptions).forEach((optionName) => {
      this.updateSelectedValue(optionName, this.selectedOptions[optionName] as string);
    });

    this.selectedVariantId = this.findVariantForSelectedOptions();

    if (this.selectedVariantId) {
      this.selectedVariant =
        this.productData?.variants?.find((variant) => variant.id === this.selectedVariantId) ??
        null;

      if (this.selectedVariant) {
        this.logger.log('Setting variant to', this.selectedVariant);
        this.quantity = 1;
      }
    }

    this.render();
  }

  private onOptionClick(event: Event) {
    if (!(event.target instanceof Element)) return;
    const input = event.target.closest<HTMLInputElement>('[data-option-input]');
    if (!input) return;

    // Ensure we process even when the radio was already selected (change may not fire)
    this.onOptionChange();
  }

  private async getProductDetails(
    productId: string | null,
  ): Promise<GetProductDetailsResponse | null> {
    if (!productId) return null;
    const response = await fetch(`${this.apiBaseUrl}/products/${productId}`);
    const data = await response.json();

    this.productDetailsJson = data as GetProductDetailsResponse;

    this.logger.log(`Got data for ${productId}:`, data);

    return data;
  }

  private bindEvents() {
    this.optionsFormElem?.addEventListener('change', this.onOptionChange.bind(this));
    this.optionsFormElem?.addEventListener('click', this.onOptionClick.bind(this));

    const decreaseQuantityButtonElem = this.decreaseQuantityButtonElem;
    const increaseQuantityButtonElem = this.increaseQuantityButtonElem;
    const closeButtonElem = this.closeButtonElem;
    const productFormElem = this.productFormElem;

    if (!decreaseQuantityButtonElem) throw new Error('Decrease quantity button element not found');
    if (!increaseQuantityButtonElem) throw new Error('Increase quantity button element not found');
    if (!closeButtonElem) throw new Error('Close button element not found');
    if (!productFormElem) throw new Error('Product form element not found');

    decreaseQuantityButtonElem.addEventListener('click', this.decreaseQuantity.bind(this));
    increaseQuantityButtonElem.addEventListener('click', this.increaseQuantity.bind(this));

    closeButtonElem.addEventListener('click', this.onClose.bind(this));

    const backdropElem = this.querySelector<HTMLElement>('.select-options-modal--backdrop');
    if (backdropElem) {
      backdropElem.addEventListener('click', this.onClose.bind(this));
    }

    productFormElem.addEventListener('submit', (e) => {
      e.preventDefault();
      this.onAddToCart();
    });
  }

  private verifyElements() {
    // Use querySelector to find all the necessary elements for functionality

    const apiBaseUrl = this.getAttribute('data-api-url');
    if (!apiBaseUrl) {
      throw new Error('data-api-url attribute not found');
    }
    this.apiBaseUrl = apiBaseUrl;

    this.swatchOptionTemplate = this.querySelector<HTMLTemplateElement>('#swatch-option');
    this.pillOptionTemplate = this.querySelector<HTMLTemplateElement>('#pill-option');

    this.closeButtonElem = this.querySelector<HTMLButtonElement>('[data-modal-close]');

    if (!this.closeButtonElem) {
      throw new Error('Close button element not found');
    }

    this.imageElem = this.querySelector<HTMLImageElement>('[data-product-image]');

    if (!this.imageElem) {
      throw new Error('Image element not found');
    }

    this.productTitleElem = this.querySelector<HTMLHeadingElement>('[data-product-title]');

    if (!this.productTitleElem) {
      throw new Error('Product title element not found');
    }

    this.variantTitleElem = this.querySelector<HTMLHeadingElement>('[data-variant-title]');

    if (!this.variantTitleElem) {
      throw new Error('Variant title element not found');
    }

    this.regularPriceElem = this.querySelector<HTMLSpanElement>('[data-price-regular] .money');

    if (!this.regularPriceElem) {
      // Retry with just the data-price-regular
      this.regularPriceElem = this.querySelector<HTMLSpanElement>('[data-price-regular]');

      // Still failed? Sadge
      if (!this.regularPriceElem) throw new Error('Could not find regular price elem');
    }

    this.compareAtPriceElem = this.querySelector<HTMLSpanElement>('[data-price-compare] .money');

    if (!this.compareAtPriceElem) {
      // Retry with just the data-price-compare
      this.compareAtPriceElem = this.querySelector<HTMLSpanElement>('[data-price-compare]');

      // Still failed? Sadge
      if (!this.compareAtPriceElem) throw new Error('Could not find compare price elem');
    }

    this.optionsFormElem = this.querySelector<HTMLFormElement>('[data-options-form]');

    if (!this.optionsFormElem) {
      throw new Error('Could not find options form elem');
    }

    this.decreaseQuantityButtonElem = this.querySelector<HTMLButtonElement>(
      '[data-decrease-quantity]',
    );

    if (!this.decreaseQuantityButtonElem) {
      throw new Error('Could not find decrease quantity button elem');
    }

    this.quantityInputElem = this.querySelector<HTMLInputElement>('[data-quantity-input]');

    if (!this.quantityInputElem) {
      throw new Error('Could not find quantity input elem');
    }

    this.increaseQuantityButtonElem = this.querySelector<HTMLButtonElement>(
      '[data-increase-quantity]',
    );

    if (!this.increaseQuantityButtonElem) {
      throw new Error('Could not find increase quantity button elem');
    }

    this.productFormElem = this.querySelector<HTMLFormElement>('[data-product-form]');

    if (!this.productFormElem) {
      throw new Error('Could not find product form elem');
    }

    this.addToCartButtonElem = this.querySelector<HTMLButtonElement>('[data-add-to-cart]');

    if (!this.addToCartButtonElem) {
      throw new Error('Could not find add to cart button elem');
    }
  }

  private renderOption(
    optionName: string,
    optionValues: GetProductDetailsData['options'][string],
    renderType: 'swatch' | 'pill' = 'pill',
  ) {
    const templateContent = (
      renderType === 'swatch'
        ? this.swatchOptionTemplate
        : renderType === 'pill'
          ? this.pillOptionTemplate
          : null
    )?.content;

    if (!templateContent) {
      throw new Error('Invalid option type!');
    }

    const clonedTemplate = templateContent.cloneNode(true) as DocumentFragment;
    const optionWrapper = clonedTemplate.querySelector<HTMLElement>('[data-option-name]');

    if (!optionWrapper) {
      throw new Error('Option wrapper not found in template');
    }

    const trimmedOptionName = optionName.trim();
    const currentSelectedValue = (this.selectedOptions[trimmedOptionName] as string | undefined)
      ?.toString()
      .trim();

    const optionValuesArray: string[] = (
      Array.isArray(optionValues) ? optionValues : Object.values(optionValues ?? {})
    ).map((value) => (typeof value === 'string' ? value.trim() : String(value ?? '').trim()));

    const trimmedOptionValue = (currentSelectedValue ?? optionValuesArray[0] ?? '').trim();

    optionWrapper.setAttribute('data-option-name', trimmedOptionName);

    const legendElem = optionWrapper.querySelector<HTMLElement>('[data-option-legend]');
    if (legendElem) legendElem.textContent = trimmedOptionName;

    const selectedValueElem = optionWrapper.querySelector<HTMLElement>(
      '[data-option-selected-value]',
    );
    if (selectedValueElem) selectedValueElem.textContent = trimmedOptionValue;

    const listElem = optionWrapper.querySelector<HTMLElement>('[data-option-list]');
    const optionItemTemplate = listElem?.querySelector<HTMLElement>('[data-option-item]');

    const slugify = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

    const resolveVariantForOptionValue = (optionValue: string) => {
      if (!this.productData?.variantOptionsMap || !this.productData?.variants) return null;

      const match = Object.entries(this.productData.variantOptionsMap).find(([, optionValues]) =>
        optionValues.includes(optionValue),
      );

      if (!match) return null;

      const variantId = match[0];
      return this.productData.variants.find((variant) => variant.id === variantId) ?? null;
    };

    const resolveSwatchData = (optionValue: string) => {
      return resolveVariantForOptionValue(optionValue)?.swatchData ?? null;
    };

    if (listElem && optionItemTemplate) {
      listElem.innerHTML = '';

      let seededDefaultValue: string | null = null;

      optionValuesArray.forEach((optionValue) => {
        const clonedItem = optionItemTemplate.cloneNode(true) as HTMLElement;
        const valueText = optionValue;
        const variantForValue = resolveVariantForOptionValue(valueText);
        const isDisabled =
          variantForValue?.inventoryPolicy === 'DENY' &&
          (variantForValue?.inventoryQuantity ?? 0) === 0;

        let isDefault = false;
        if (currentSelectedValue) {
          isDefault = valueText === currentSelectedValue && !isDisabled;
        } else if (!seededDefaultValue && !isDisabled) {
          isDefault = true;
          seededDefaultValue = valueText;
        }

        const input = clonedItem.querySelector<HTMLInputElement>('[data-option-input]');
        if (input) {
          input.name = trimmedOptionName;
          input.value = valueText;
          input.id = `${renderType}-${slugify(trimmedOptionName)}-${slugify(valueText)}`;
          input.checked = isDefault;
          input.defaultChecked = isDefault;
          input.disabled = isDisabled;
        }

        const label = clonedItem.querySelector<HTMLLabelElement>('[data-option-label]');
        if (label) {
          label.setAttribute('for', input?.id ?? '');
          label.title = valueText;
        }

        const srOnlyElem = clonedItem.querySelector<HTMLElement>('[data-option-sr-only]');
        if (srOnlyElem) srOnlyElem.textContent = valueText;

        const visualElem = clonedItem.querySelector<HTMLElement>('[data-option-visual]');
        if (visualElem) {
          visualElem.textContent = valueText;

          if (renderType === 'swatch') {
            const swatchData = resolveSwatchData(valueText);
            const color = swatchData?.colour ?? '';
            const image = swatchData?.image ?? '';

            visualElem.style.backgroundColor = color || '';
            visualElem.style.backgroundImage = image ? `url(${image})` : '';
          }
        }

        listElem.insertAdjacentElement('beforeend', clonedItem);
      });

      // Seed default selection into state when none exists so FormData captures it
      if (!currentSelectedValue) {
        const valueToSeed = seededDefaultValue ?? optionValuesArray[0];
        if (valueToSeed) {
          this.selectedOptions[trimmedOptionName] = valueToSeed as FormDataEntryValue;
        }
      }
    }

    return optionWrapper;
  }

  private renderOptions(root: HTMLElement) {
    // For each option in this.productData?.options, check whether the option name is in the config's colourSwatchOptionsList and render accordingly

    if (!this.productData?.options) return;

    Object.entries(this.productData.options).forEach(([optionName, optionValues]) => {
      if (
        window.theme.selectOptionsModalConfig?.colourSwatchOptionsList.find((swatchOption) => {
          return swatchOption.toLowerCase().trim() === optionName.toLowerCase().trim();
        })
      ) {
        this.logger.log(`Rendering swatch option for ${optionName}`);
        const optionElem = this.renderOption(optionName, optionValues, 'swatch');
        const existing = root.querySelector<HTMLElement>(`[data-option-name="${optionName}"]`);
        if (existing) {
          existing.replaceWith(optionElem);
        } else {
          root.insertAdjacentElement('beforeend', optionElem);
        }
      } else {
        this.logger.log(`Rendering pill option for ${optionName}`);
        const optionElem = this.renderOption(optionName, optionValues, 'pill');
        const existing = root.querySelector<HTMLElement>(`[data-option-name="${optionName}"]`);
        if (existing) {
          existing.replaceWith(optionElem);
        } else {
          root.insertAdjacentElement('beforeend', optionElem);
        }
      }
    });
  }

  private updateStaticElements() {
    const imageElem = this.imageElem;
    const productTitleElem = this.productTitleElem;
    const variantTitleElem = this.variantTitleElem;
    const regularPriceElem = this.regularPriceElem;
    const compareAtPriceElem = this.compareAtPriceElem;

    if (!imageElem) throw new Error('Image element not found');
    if (!productTitleElem) throw new Error('Product title element not found');
    if (!variantTitleElem) throw new Error('Variant title element not found');
    if (!regularPriceElem) throw new Error('Regular price element not found');
    if (!compareAtPriceElem) throw new Error('Compare-at price element not found');

    imageElem.src =
      this.selectedVariant !== null ? this.selectedVariant.image : (this.productData?.image ?? '');
    imageElem.srcset = `${this.selectedVariant !== null ? this.selectedVariant.image : (this.productData?.image ?? '')} 180w`;

    productTitleElem.textContent = this.productData?.title ?? '';
    variantTitleElem.textContent = this.selectedVariant?.title ?? '';

    const displayMoneyFormat = this.getDisplayMoneyFormat();
    const regularPriceBaseCents = this.selectedVariant?.price ?? 0;
    const regularPriceActiveCents = this.convertBaseCentsToActiveCents(regularPriceBaseCents);

    regularPriceElem.innerHTML = shopifyFormatCurrency(regularPriceActiveCents, displayMoneyFormat);

    // Hide if compareAtPrice is null, show if not null
    compareAtPriceElem.classList.toggle('hidden', !this.selectedVariant?.compareAtPrice);

    const compareAtPriceBaseCents = this.selectedVariant?.compareAtPrice ?? 0;
    const compareAtPriceActiveCents = this.convertBaseCentsToActiveCents(compareAtPriceBaseCents);

    compareAtPriceElem.innerHTML = shopifyFormatCurrency(
      compareAtPriceActiveCents,
      displayMoneyFormat,
    );
  }

  private updateProductForm() {
    const quantityInput = this.querySelector<HTMLInputElement>('[data-quantity-input]');
    const idInput = this.querySelector<HTMLInputElement>(`[name="id"]`);

    if (!quantityInput) throw new Error('Quantity input element not found');
    if (!idInput) throw new Error('Variant id input element not found');

    quantityInput.value = this.quantity.toString();
    idInput.value = this.selectedVariantId;
  }

  private render() {
    if (!this.optionsFormElem) throw new Error('Options form element not found');
    this.updateStaticElements();
    this.renderOptions(this.optionsFormElem);
    this.updateProductForm();
  }

  connectedCallback() {
    this.verifyElements();

    this.bindEvents();

    this.handleRequest(this.productId, false);
  }

  handleRequest(productId: string | null, autoOpen: boolean = true) {
    new Promise<GetProductDetailsResponse>((resolve, reject) => {
      try {
        if (this.productId === productId && this.productData) {
          this.productDetailsJson = {
            status: 'success',
            data: this.productData,
          };
          return resolve({
            status: 'success',
            data: this.productData,
          });
        }

        return this.getProductDetails(productId).then((data) => {
          if (!data) {
            return reject(new Error('Could not fetch product details!'));
          }

          if (data.status !== 'success') {
            return reject(new Error(`Failed to fetch product details! ${data.message}`));
          }

          return resolve({
            status: 'success',
            data: data.data,
          });
        });
      } catch (e) {
        return reject(e);
      }
    })
      .then((result) => {
        if (!result) {
          throw new Error('Could not fetch product details!');
        }

        if (result.status !== 'success') {
          throw new Error(`Failed to fetch product details! ${result.message}`);
        }

        if (!productId) throw new Error('Missing productId');

        const productFormElem = this.productFormElem;
        if (!productFormElem) throw new Error('Product form element not found');

        const productIdInput =
          productFormElem.querySelector<HTMLInputElement>(`[name="product-id"]`);
        if (!productIdInput) throw new Error('Product id input element not found');

        this.productId = productId;
        productIdInput.value = this.productId;

        this.productData = result.data;

        // Reset option selections for new product to avoid stale selected value text
        this.selectedOptions = {};

        this.selectedVariantId = this.productData?.variants[0].id ?? '';

        this.selectedVariant = this.productData?.variants[0];

        this.quantity = 1;
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        const optionsFormElem = this.optionsFormElem;
        if (optionsFormElem) {
          optionsFormElem.innerHTML = '';
        }

        this.render();

        if (autoOpen) {
          this.showPopover();
        }
      });
  }
}

if (customElements.get('select-options') === undefined) {
  customElements.define('select-options', SelectOptionsModal);
}

window.selectOptionsHandler = {
  handleRequest: (productId: string) => {
    const modalElem = document.querySelector<SelectOptionsModal>('select-options');

    if (modalElem) {
      modalElem.handleRequest(productId);
    }
  },
};
